import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { SalesDateRange } from '../../../api/daily-sales';

import {
  presetOf,
  presetRange,
  rangeFromParams,
  salesToday,
  type RangePreset,
} from './daily-sales-range';

/**
 * The Daily sales date range, kept in the address (`?from&to`) so a reload or a shared
 * link opens the same dates. The default window leaves the address empty. Each change
 * is a new history entry, so back and forward step between ranges.
 */
export function useDailySalesRange() {
  const [params, setParams] = useSearchParams();
  const today = salesToday();
  const range = rangeFromParams(params);
  const rangeKey = `${range?.from}|${range?.to}`;
  // Picking Custom on a preset's dates opens the date fields without changing the range;
  // that holds only until the range changes, by any route including back and forward.
  const [customOpenedAt, setCustomOpenedAt] = useState<string | null>(null);
  const matched = presetOf(range, today);
  const preset: RangePreset = customOpenedAt === rangeKey ? 'custom' : matched;

  const setRange = (next: SalesDateRange | null) => {
    setParams(next ? new URLSearchParams({ from: next.from, to: next.to }) : new URLSearchParams());
  };

  const choosePreset = (next: RangePreset) => {
    if (next === 'custom') setCustomOpenedAt(rangeKey);
    else setRange(presetRange(next, today));
  };

  return { today, range, preset, choosePreset, setRange };
}
